/* Lumetier theme registry — Phase: per-studio personalization.

   Each studio's `site_settings` holds a `theme_id` + an optional `theme_overrides`
   object (per-key hex overrides). We resolve the final palette by merging the
   preset palette with the studio's overrides, then push it into the shadcn-style
   HSL CSS variables that both the public studio page and the Studio Manager
   admin chrome read from (--background, --foreground, --primary, --accent, etc.).

   Theme palette keys (hex values, user-friendly):
     - background        page background
     - foreground        page text
     - primary           CTA background (also serves as headers/footers)
     - primary_foreground  text on primary
     - accent            small editorial accents (italic word, eyebrow, gold rule)
     - muted             low-contrast surface (about/contact panels)

   Card, border, ring, muted-foreground are derived from these 6 anchors so
   the studio owner only needs to think about a few colors. */

export const THEMES = [
  // ── Originals ────────────────────────────────────────────────────────────
  {
    id: "editorial",
    name: "Editorial Gold",
    description: "Warm ivory, charcoal, champagne gold. The signature Allure look.",
    palette: {
      background: "#FAF9F6",
      foreground: "#1C1917",
      primary: "#292524",
      primary_foreground: "#FAF9F6",
      accent: "#C9A961",
      muted: "#F2F0EE",
      ui_accent: "#D4C5B9",
    },
  },
  {
    id: "modern-minimal",
    name: "Modern Minimal",
    description: "Crisp white, deep slate, electric blue. Clean and contemporary.",
    palette: {
      background: "#FFFFFF",
      foreground: "#0F172A",
      primary: "#0F172A",
      primary_foreground: "#FFFFFF",
      accent: "#2563EB",
      muted: "#F1F5F9",
    },
  },
  {
    id: "warm-boutique",
    name: "Warm Boutique",
    description: "Peach cream, terracotta, dusty rose. Inviting and feminine.",
    palette: {
      background: "#FFF7F0",
      foreground: "#3D2B1F",
      primary: "#9A3412",
      primary_foreground: "#FFF7F0",
      accent: "#DB7E62",
      muted: "#FCEBDC",
    },
  },
  {
    id: "bold-dark",
    name: "Bold Dark",
    description: "Near-black, ivory text, amber accent. Premium and dramatic.",
    palette: {
      background: "#0C0A09",
      foreground: "#FAFAF9",
      primary: "#FAFAF9",
      primary_foreground: "#0C0A09",
      accent: "#F59E0B",
      muted: "#1C1917",
    },
  },
  {
    id: "soft-pastel",
    name: "Soft Pastel",
    description: "Lavender mist, deep violet, sage. Calm and approachable.",
    palette: {
      background: "#FAF5FF",
      foreground: "#1E1B4B",
      primary: "#6D28D9",
      primary_foreground: "#FAF5FF",
      accent: "#84CC16",
      muted: "#F3E8FF",
    },
  },
  {
    id: "forest-earth",
    name: "Forest Earth",
    description: "Stone cream, deep forest, mossy green. Grounded and organic.",
    palette: {
      background: "#F7F5F0",
      foreground: "#1A2E1A",
      primary: "#2D5016",
      primary_foreground: "#F7F5F0",
      accent: "#85A332",
      muted: "#ECEEE2",
    },
  },
  {
    id: "coastal-blue",
    name: "Coastal Blue",
    description: "Ivory, navy, seafoam. Fresh and timeless.",
    palette: {
      background: "#F8F9FB",
      foreground: "#0F2942",
      primary: "#1E3A5F",
      primary_foreground: "#F8F9FB",
      accent: "#5EAAA8",
      muted: "#E8EEF2",
    },
  },
  {
    id: "vibrant-sunset",
    name: "Vibrant Sunset",
    description: "Coral cream, plum, golden hour. Bold and creative.",
    palette: {
      background: "#FFF4F0",
      foreground: "#2D1B3D",
      primary: "#7C2D5C",
      primary_foreground: "#FFF4F0",
      accent: "#F97316",
      muted: "#FFE4D6",
    },
  },

  // ── New themes ────────────────────────────────────────────────────────────
  {
    id: "rose-gold",
    name: "Rose Gold",
    description: "Blush ivory, deep burgundy, liquid rose gold. Romantic and refined.",
    palette: {
      background: "#FEF5F2",
      foreground: "#3A1B22",
      primary: "#7E2D42",
      primary_foreground: "#FEF5F2",
      accent: "#C47C77",
      muted: "#FAEBE6",
    },
  },
  {
    id: "midnight",
    name: "Midnight",
    description: "Deep navy-black, cool pearl, electric teal. Sleek and sophisticated.",
    palette: {
      background: "#080C14",
      foreground: "#E4EAF4",
      primary: "#E4EAF4",
      primary_foreground: "#080C14",
      accent: "#38BDF8",
      muted: "#0D1422",
    },
  },
  {
    id: "sage-garden",
    name: "Sage Garden",
    description: "Linen white, deep forest sage, warm olive. Natural and elevated.",
    palette: {
      background: "#F5F5EE",
      foreground: "#1C2B1A",
      primary: "#2B4A28",
      primary_foreground: "#F5F5EE",
      accent: "#7D9E68",
      muted: "#E8EDE0",
    },
  },
  {
    id: "mauve-luxe",
    name: "Mauve Luxe",
    description: "Soft blush, deep plum, dusty mauve. Understated glamour.",
    palette: {
      background: "#F9F4F7",
      foreground: "#2A1428",
      primary: "#5C2460",
      primary_foreground: "#F9F4F7",
      accent: "#A0688A",
      muted: "#F0E6EE",
    },
  },
  {
    id: "obsidian",
    name: "Obsidian",
    description: "True black, warm cream, pure gold. Uncompromising luxury.",
    palette: {
      background: "#080807",
      foreground: "#EDE8E0",
      primary: "#EDE8E0",
      primary_foreground: "#080807",
      accent: "#D4A73A",
      muted: "#121210",
    },
  },
  {
    id: "desert-dunes",
    name: "Desert Dunes",
    description: "Warm sand, deep tobacco, spiced caramel. Sun-kissed and grounded.",
    palette: {
      background: "#F8F1E4",
      foreground: "#2D1B0A",
      primary: "#4A2808",
      primary_foreground: "#F8F1E4",
      accent: "#C47A2C",
      muted: "#EFE1C8",
    },
  },
];

export const DEFAULT_THEME_ID = "editorial";

export function getThemeById(id) {
  return THEMES.find((t) => t.id === id) || THEMES[0];
}

/* The six keys a studio owner can override individually. Keep this list in sync
   with the palette object above + with the override editor UI on /admin/site. */
export const THEME_KEYS = [
  { key: "background", label: "Page background" },
  { key: "foreground", label: "Body text" },
  { key: "primary", label: "Primary (buttons)" },
  { key: "primary_foreground", label: "Text on primary" },
  { key: "accent", label: "Accent / highlight" },
  { key: "muted", label: "Muted surface" },
];

/* Resolve final palette = preset merged with per-key overrides.
   Empty / missing override values fall back to the preset. */
export function resolvePalette(themeId, overrides = {}) {
  const base = getThemeById(themeId).palette;
  const merged = { ...base };
  for (const k of Object.keys(overrides || {})) {
    const v = overrides[k];
    if (v && typeof v === "string" && v.trim().length > 0) {
      merged[k] = v.trim();
    }
  }
  return merged;
}
